import axios, { AxiosResponse } from 'axios';
import CONFIG from '@/config';
import AuthUserStore from '@/common/utils/AuthUser';
import { formDataInstance } from '../axios';

export async function setApiVersion() {
  const res = await axios.get(CONFIG.API + '/version/', {
    headers: getRestHeaders()
  });
  AuthUserStore.setBuildVersion(res?.data?.version);
  return res?.data?.version || 'Not Available';
}

export function getRestHeaders(additionalHeaders = undefined, prefix = 'JWT') {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: prefix + ' ' + (localStorage.getItem('RES_AUTH_TOKEN') || '')
  };
  if (additionalHeaders) {
    for (const h in additionalHeaders) {
      headers[h] = additionalHeaders[h];
    }
  }
  return headers;
}

export const callRestGetEndpoint = async <T = any>(
  postArgs: T,
  endpoint: string
) => {
  const res = await formDataInstance.get(CONFIG.API + endpoint, {
    params: postArgs,
    headers: getRestHeaders()
  });
  if (res.data?.status === 'success') {
    return res.data;
  } else {
    // who's to say we don't want some of the response if non-success?  this is silly
    return false;
  }
};

export const callRestPostEndpoint = async <T = any>(
  postArgs: T,
  endpoint: string,
  headers?: any
): Promise<AxiosResponse> => {
  return axios
    .post(CONFIG.API + endpoint, postArgs, {
      headers: getRestHeaders(headers)
    })
    .then(res => res.data)
    .catch(e => {
      throw new Error(e.response.data?.error || 'Something went wrong.');
    });
};

export const isAxiosError = axios.isAxiosError;

export const callRestPatchEndpoint = async <T = Record<string, unknown>>(
  patchArgs: T,
  endpoint: string,
  headers?: Record<string, string>
) => {
  return axios
    .patch(CONFIG.API + endpoint, patchArgs, {
      headers: getRestHeaders(headers)
    })
    .then(res => res.data)
    .catch(e => {
      throw new Error(e.response.data?.error || 'Something went wrong.');
    });
};

const postRequest = <T>(
  endpoint: string,
  data: any,
  config?: any
): Promise<AxiosResponse<T>> => {
  return axios.post(`${CONFIG.API}/${endpoint}`, data, {
    ...(config || {}),
    headers: getRestHeaders()
  });
};

const createPostApiFunction = <Params, ReturnType, ConfigType = any>(
  endpoint: string
) => {
  return async (
    data: Params,
    config: ConfigType = {} as ConfigType
  ): Promise<ReturnType> => {
    try {
      const res = await postRequest<ReturnType>(endpoint, data, config);
      return res?.data;
    } catch (e) {
      console.error(e);
    }
  };
};
export type ApiType = {
  create_setup_intent: {
    params: { customer: string };
    return_type: { client_secret?: string; error?: string };
  };
  save_payment_method_from_setup_id: {
    params: {
      customer: string;
      paymentMethod: string;
      store: string;
    };
    return_type: { error: string; payment_method: number };
  };
  add_payment_method_from_id: {
    params: {
      customer: string;
      paymentMethod: string;
      makeDefault: boolean;
      store: string;
      nickname?: string;
    };
    return_type: { paymentMethod?: string; error?: string };
  };
  create_intent_for_reader: {
    params: {
      customer_id: number;
      stripe_intent_id?: string;
      stripe_reader_id: string;
      amount: number;
      save_for_later: boolean;
    };
    return_type: {
      stripe_intent_id?: string;
      message?: string;
      success: boolean;
    };
  };
  get_reader_intent_status: {
    params: { stripe_intent_id: string };
    return_type: {
      success: boolean;
      message: string;
      stripe_payment_method_id: string;
      stripe_intent_id: string;
    };
  };
  cancel_intent_for_reader: {
    params: {
      stripe_intent_id: string;
      stripe_reader_id: string;
      customer_id: number;
    };
    return_type: void;
  };
  get_available_readers: {
    params: { stripe_location_id: string };
    return_type: {
      readers: { id: string }[];
      success: boolean;
      message?: string;
    };
  };
  get_swipe_status: {
    params: { stripe_reader_id: string; stripe_intent_id: string };
    return_type: {
      reader: {
        status: string;
        action: {
          status: string;
          failure_message: string;
          failure_code: string;
        };
      };
      intent: {
        status: string;
        id: string;
      };
    };
  };
};

const pos_stripe = {
  create_setup_intent: createPostApiFunction<
    ApiType['create_setup_intent']['params'],
    ApiType['create_setup_intent']['return_type']
  >('stripe/create_setup_intent/'),
  save_payment_method_from_setup_id: createPostApiFunction<
    ApiType['save_payment_method_from_setup_id']['params'],
    ApiType['save_payment_method_from_setup_id']['return_type']
  >('stripe/save_payment_method_from_setup_id/'),
  add_payment_method_from_id: createPostApiFunction<
    ApiType['add_payment_method_from_id']['params'],
    ApiType['add_payment_method_from_id']['return_type']
  >('stripe/add_payment_method_from_id/'),
  create_intent_for_reader: createPostApiFunction<
    ApiType['create_intent_for_reader']['params'],
    ApiType['create_intent_for_reader']['return_type']
  >('stripe/create_intent_for_reader/'),
  get_reader_intent_status: createPostApiFunction<
    ApiType['get_reader_intent_status']['params'],
    ApiType['get_reader_intent_status']['return_type']
  >('stripe/get_reader_intent_status/'),
  cancel_intent_for_reader: createPostApiFunction<
    ApiType['cancel_intent_for_reader']['params'],
    ApiType['cancel_intent_for_reader']['return_type']
  >('stripe/cancel_intent_for_reader/'),
  get_available_readers: createPostApiFunction<
    ApiType['get_available_readers']['params'],
    ApiType['get_available_readers']['return_type']
  >('stripe/get_available_readers/'),
  get_swipe_status: createPostApiFunction<
    ApiType['get_swipe_status']['params'],
    ApiType['get_swipe_status']['return_type']
  >('stripe/get_swipe_status/')
};

export const PosBackendRest = {
  pos_stripe
};
